import { copyToClipboard } from 'plugin/js/main/utils';
import Component from '../../Component';
import BillTrackerFilters, {
	BillTrackerFiltersState,
	defaultFilters,
} from './filters';
import BillTrackerMap from './map';
import { BillData, getSingleStateProperty, SortParam } from './source';
import BillTrackerTable from './table';
import BillTrackerList from './list';
import { getParams, replaceParams } from '../url';

export interface BillTrackerState {
	activeView?: string;
	activeLegislationType?: string;
	activeState?: string;
	activeSort?: string[];
	activeCategories?: Array<string>;
}

export interface BillTrackerParamData extends BillTrackerState {
	id?: string;
	post?: string;
}

class BillTracker extends Component<HTMLDivElement, BillTrackerState> {
	static selector = '.bill-tracker';
	static fromPageParamKey = 'post';
	static billTrackerPageSlug = 'bill-tracker';

	static children = {
		map: '.bill-tracker__map',
		loading: '.bill-tracker__loading',
		table: '.bill-tracker__table',
		filters: '.bill-tracker__filters',
		list: '.bill-tracker__list',
		controls: '.bill-tracker__controls',
		summary: '.bill-tracker__summary',
		cta: '.bill-tracker__cta',
	};

	static members = {
		table: BillTrackerTable,
		filters: BillTrackerFilters,
		map: BillTrackerMap,
		list: BillTrackerList,
	};

	id: string;

	exportTableButton?: HTMLAnchorElement;
	exportMapButton?: HTMLButtonElement;
	shareButton?: HTMLAnchorElement;

	state: BillTrackerState = {
		activeView: 'map',
		activeLegislationType: 'bill',
		activeState: 'all',
		activeSort: defaultFilters.sort,
		activeCategories: ['all'],
	};

	constructor(element: HTMLDivElement) {
		super(element);

		this.id = element.getAttribute('id') || '';
	}

	async afterInit() {
		this.initShare();
		this.members?.map.on('init', this.onMapInit.bind(this));
		this.members?.map.on('render', this.onMapRender.bind(this));
		this.members?.filters.onStateChange(
			this.onFiltersStateChange.bind(this)
		);
		this.members?.filters.on('init', this.onFiltersInit.bind(this));
		this.members?.table.on('render', this.onTableRender.bind(this));
		this.members?.table.on('columnsort', this.onTableColumnSort.bind(this));
		this.members?.table.on('init', this.onTableInit.bind(this));
		this.members?.list.on('init', this.sortList.bind(this));
	}

	// Initialize share button
	initShare() {
		if (!this.shareButton) {
			this.shareButton = Object.assign(document.createElement('a'), {
				className: 'page-btn btn bill-tracker__share',
				innerHTML: `
					<span>Share</span>
					<span>Link copied</span>
				`,
			});

			this.shareButton.addEventListener(
				'click',
				this.onShareClick.bind(this)
			);

			this.children?.controls.appendChild(this.shareButton);

			// Parse initial filter values from query
			const params = getParams() as Partial<BillTrackerParamData>;

			const currentPageId =
				document
					.querySelector('meta[name="post-id"]')
					?.getAttribute('content') ||
				params?.post ||
				'';

			const postId = currentPageId || params?.post || '';

			if (postId) {
				this.setShareParams({ post: postId });
			}
		}
	}

	onMapInit() {
		this.element.classList.remove('bill-tracker--loading');
		this.members?.map.setFilter(this.filterMapBill.bind(this));

		// Initialize export button
		if (!this.exportMapButton) {
			this.exportMapButton = Object.assign(
				document.createElement('button'),
				{
					className:
						'page-btn btn bill-tracker__export-map trevor-ti-download',
					textContent: 'Download',
					type: 'button',
				}
			);

			this.exportMapButton.addEventListener(
				'click',
				this.exportMap.bind(this)
			);

			this.shareButton?.insertAdjacentElement(
				'beforebegin',
				this.exportMapButton
			);
		}
	}

	onMapRender() {
		this.updateSummary();
	}

	onTableInit() {
		// Initialize export button
		this.exportTableButton = Object.assign(document.createElement('a'), {
			className: 'page-btn btn bill-tracker__export-table',
			textContent: 'Export Data',
			download: 'Bill Tracker Data.csv',
			target: '_blank',
		});

		this.shareButton?.insertAdjacentElement(
			'beforebegin',
			this.exportTableButton
		);
	}

	onTableRender() {
		this.updateExportTableButtonValues();
		this.updateSummary();
	}

	onTableColumnSort(event: CustomEvent & { detail: SortParam }) {
		const { column, order } = event.detail;
		const selectedSort = [column, order].join(':');
		const [activeSort] = this.state.activeSort?.slice(0) || [];

		// Skip if same column/order
		if (activeSort === selectedSort) {
			return;
		}

		this.members?.filters.setFilters({
			sort: selectedSort,
		});
	}

	updateExportTableButtonValues() {
		const csvData = this.members?.table.getCSVData();

		this.exportTableButton?.setAttribute(
			'href',
			`data:;text/csv,${csvData}`
		);
	}

	sortList() {
		const { activeSort } = this.state;
		if (activeSort) {
			const sortParams = activeSort.map((sortValue) => {
				const [column, order] = sortValue.split(':');
				return { column, order };
			});

			this.members?.table.sortTable(sortParams);
			this.members?.list.sortList(sortParams);
		}
	}

	onFiltersStateChange(filtersChangedState: BillTrackerFiltersState) {
		const mappedStates = Object.entries(filtersChangedState).reduce(
			(changedStates: BillTrackerState, [key, value]) => {
				switch (key) {
					case 'view':
						changedStates.activeView = value;
						break;
					case 'state':
						changedStates.activeState = value;
						break;
					case 'sort':
						changedStates.activeSort = value;
						break;
					case 'legislationType':
						changedStates.activeLegislationType = value;
						break;
					case 'categories':
						changedStates.activeCategories = value;
						break;
				}

				return changedStates;
			},
			{}
		);

		this.setState(mappedStates);
	}

	filterListBill(bill: BillData) {
		const { activeState = 'all' } = this.state;
		const localityCode = `US-${activeState}`.toUpperCase();

		return (
			(activeState === 'all' || bill.locality_code === localityCode) &&
			this.filterBillByLegislationType(bill) &&
			this.filterBillByActiveCategory(bill)
		);
	}

	filterMapBill(bill: BillData) {
		const { activeState = 'all' } = this.state;
		const localityCode = `US-${activeState}`.toUpperCase();

		return (
			(activeState === 'all' || bill.locality_code === localityCode) &&
			this.filterBillByLegislationType(bill) &&
			this.filterBillByActiveCategory(bill)
		);
	}

	filterBillByLegislationType(bill: BillData) {
		const { activeLegislationType } = this.state;

		return activeLegislationType === 'law'
			? bill.is_current_law
			: !bill.is_current_law;
	}

	filterBillByActiveCategory(bill: BillData) {
		const { activeCategories } = this.state;

		return (
			!activeCategories?.length ||
			activeCategories[0] === 'all' ||
			(activeCategories.includes('allPro') &&
				bill.category_group === 'pro') ||
			(activeCategories.includes('allAnti') &&
				bill.category_group === 'anti') ||
			activeCategories.some((billCategory) =>
				bill.categories.includes(billCategory)
			)
		);
	}

	async updateSummary() {
		const {
			activeView,
			activeLegislationType = 'bill',
			activeState = 'all',
		} = this.state;

		if (!this.children?.summary) {
			return;
		}

		const location =
			activeView === 'map' || activeState === 'all'
				? 'All Legislation'
				: await getSingleStateProperty(
						activeState.toUpperCase(),
						'name'
				  );

		const totalBillsInView =
			activeView === 'list'
				? this.members?.table.state.billsInView.length || 0
				: this.members?.map.getBillsInViewTotal();

		this.children.summary.textContent = [
			location,
			`${totalBillsInView} ${
				activeLegislationType?.charAt(0)?.toUpperCase() +
				activeLegislationType?.slice(1)
			}${totalBillsInView !== 1 ? 's' : ''}`,
		].join(', ');

		this.element.classList.toggle(
			'bill-tracker--empty',
			totalBillsInView === 0
		);
	}

	exportMap() {
		this.members?.map.export();
	}

	onFiltersInit() {
		// Parse initial filter values from query
		const params = getParams() as Partial<BillTrackerParamData>;

		if (!Object.keys(params).length) {
			return;
		}

		const data: BillTrackerParamData = params;

		if (data && data.id === this.id) {
			// Convert query data into Filter state data, and apply state.
			const currentFilterState: BillTrackerFiltersState =
				this.members?.filters.state || {};
			const queryAsState = {
				view: data.activeView || currentFilterState.view || 'map',
				legislationType:
					data.activeLegislationType ||
					currentFilterState.legislationType ||
					'bills',
				state: data.activeState || currentFilterState.state || 'all',
				categories:
					data.activeCategories ||
					currentFilterState.categories ||
					[],
				sort: data.activeSort || currentFilterState.sort || '',
			};

			this.members?.filters.setFilters(queryAsState);
		} else {
			this.componentDidUpdate();
		}
	}

	updateQueryParams() {
		let paramsData: { [key: string]: any } = {};
		const billTrackerParams = getParams(
			'',
			(this.shareButton as Partial<HTMLAnchorElement>).href
		) as Partial<BillTrackerParamData>;

		if (billTrackerParams && billTrackerParams.id === this.id) {
			// Apply new state onto matching bill tracker param data
			Object.entries(this.state).forEach(([key, data]) => {
				billTrackerParams[key as keyof BillTrackerParamData] =
					data as string & string[];
			});

			paramsData = billTrackerParams;
		} else {
			paramsData = {
				...this.state,
				id: this.id,
			};
		}

		// Normalize param values
		Object.keys(paramsData).forEach((key) => {
			if (Array.isArray(paramsData[key])) {
				paramsData[key] = JSON.stringify(paramsData[key]);
			}
		});

		this.setShareParams(paramsData);
	}

	setShareParams(paramsData: Record<string, string>) {
		this.shareButton?.setAttribute(
			'href',
			replaceParams(
				paramsData,
				false,
				(this.shareButton as HTMLAnchorElement & []).href ||
					location.origin + '/bill-tracker/'
			)
		);
	}

	onShareClick(event: MouseEvent) {
		event.preventDefault();

		if (this.shareButton) {
			copyToClipboard(
				(this.shareButton as unknown as HTMLAnchorElement).href
			);

			this.shareButton.setAttribute('data-copied', 'true');

			setTimeout(() => {
				this.shareButton?.removeAttribute('data-copied');
			}, 5000);
		}
	}

	componentDidUpdate(changedStates?: Partial<BillTrackerState>) {
		this.element.setAttribute('data-view', this.state.activeView as string);

		if (changedStates) {
			this.updateQueryParams();

			if ('activeSort' in changedStates) {
				this.sortList();
			} else if (
				'activeState' in changedStates ||
				'activeLegislationType' in changedStates ||
				'activeCategories' in changedStates
			) {
				this.members?.list.setFilter(this.filterListBill.bind(this));
				this.members?.table.setFilter(this.filterListBill.bind(this));
				this.members?.map.setFilter(this.filterMapBill.bind(this));
				this.members?.map.closeTooltip();
			} else if ('activeView' in changedStates) {
				const isListView = changedStates.activeView === 'list';
				this.updateSummary();

				if (!isListView) {
					this.members?.table.clearRows();
					this.members?.list.clearItems();
				}

				this.members?.list.setState({ visible: isListView });
				this.members?.table.setState({ visible: isListView });
				this.members?.map.setState({ visible: !isListView });
			}
		}
	}
}

BillTracker.init();
